import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../api/client'
import type { components } from '../../api/schema'
import type { Definition, DefinitionSpec, View } from './definition'

export type PreviewResult = components['schemas']['ThematicPreviewResult']
export type DefinitionResult = components['schemas']['ThematicDefinitionResult']

/** An API refusal, with the field errors of a validation. */
export class ApiFailure extends Error {
  fields: { name: string, message: string }[]
  constructor(message: string, fields: { name: string, message: string }[] = []) {
    super(message)
    this.fields = fields
  }
}

/** Turns an error body (`{title, fields: [{name, message}]}`) into an ApiFailure. */
function failure(error: unknown): ApiFailure {
  const body = (error ?? {}) as { title?: string, message?: string, fields?: { name?: string, message: string }[] }
  return new ApiFailure(
    body.title ?? body.message ?? 'Request failed',
    (body.fields ?? []).map((d) => ({ name: d.name ?? '', message: d.message })),
  )
}

const LIST = '/api/v1/organizations/{orgCode}/thematic-definitions' as const
const ONE = '/api/v1/organizations/{orgCode}/thematic-definitions/{code}' as const
const PREVIEW = '/api/v1/organizations/{orgCode}/assets/{assetUid}/thematics/preview' as const

export const definitionKeys = {
  all: (org: string) => ['thematics', 'definitions', org] as const,
  one: (org: string, code: string) => ['thematics', 'definitions', org, code] as const,
}

export function useThematicDefinitions(org: string) {
  return useQuery({
    queryKey: definitionKeys.all(org),
    queryFn: async () => {
      const { data, error } = await apiClient.GET(LIST, { params: { path: { orgCode: org } } })
      if (error) throw failure(error)
      return (data?.definitions ?? []) as Definition[]
    },
    enabled: !!org,
  })
}

export function useThematicDefinition(org: string, code: string | undefined) {
  return useQuery({
    queryKey: definitionKeys.one(org, code ?? ''),
    queryFn: async () => {
      const { data, error } = await apiClient.GET(ONE, { params: { path: { orgCode: org, code: code as string } } })
      if (error) throw failure(error)
      return data as Definition
    },
    enabled: !!org && !!code,
    retry: false,
  })
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  // The definitions and every catalogue and legend that lists them.
  return qc.invalidateQueries({ queryKey: ['thematics'] })
}

/** Creates (`code` unset) or replaces (`code` set) a definition. */
export function useSaveThematicDefinition(org: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ code, spec }: { code?: string, spec: DefinitionSpec }) => {
      if (code) {
        const { data, error } = await apiClient.PUT(ONE, { params: { path: { orgCode: org, code } }, body: spec })
        if (error) throw failure(error)
        return data as DefinitionResult
      }
      const { data, error } = await apiClient.POST(LIST, { params: { path: { orgCode: org } }, body: spec })
      if (error) throw failure(error)
      return data as DefinitionResult
    },
    onSuccess: () => invalidate(qc),
  })
}

export function useDeleteThematicDefinition(org: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (code: string) => {
      const { error } = await apiClient.DELETE(ONE, { params: { path: { orgCode: org, code } } })
      if (error) throw failure(error)
    },
    onSuccess: () => invalidate(qc),
  })
}

/** The legend a draft would give on one asset's view. */
export function usePreviewThematicDefinition(org: string) {
  return useMutation({
    mutationFn: async ({ assetUid, view, definition }: { assetUid: string, view: View, definition: DefinitionSpec }) => {
      const { data, error } = await apiClient.POST(PREVIEW, {
        params: { path: { orgCode: org, assetUid } },
        body: { view, definition },
      })
      if (error) throw failure(error)
      return data as PreviewResult
    },
  })
}
